import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Send, 
  Bot, 
  User, 
  Check, 
  X, 
  MessageCircle,
  Loader2,
  Trash2
} from 'lucide-react';
import { useChatbot } from '@/hooks/useChatbot';
import { ChatMessage, ChatSuggestion } from '@/lib/schemas/chatbot';
import { useTranslation } from 'react-i18next';
import { parseISO, format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import i18n from '@/lib/i18n';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { useTenant } from '@/hooks/useTenant';

interface AIChatbotSidebarProps {
  className?: string;
  onClose?: () => void;
}

// Helper function to format date and time in German format
const formatBlockTimeDisplay = (startTime: string, endTime: string) => {
  const start = parseISO(startTime);
  const end = parseISO(endTime);
  
  // Format: 6.9.2025 12:00 - 13:00
  // eslint-disable-next-line sylva-i18n/require-i18n-date-keys
  const dateStr = start.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', { 
    day: 'numeric', 
    month: 'numeric', 
    year: 'numeric' 
  });
  // eslint-disable-next-line sylva-i18n/require-i18n-date-keys
  const startTimeStr = start.toLocaleTimeString(i18n.language === 'de' ? 'de-DE' : 'en-US', { 
    hour: '2-digit', 
    minute: '2-digit', 
    hour12: i18n.language !== 'de' 
  });
  // eslint-disable-next-line sylva-i18n/require-i18n-date-keys
  const endTimeStr = end.toLocaleTimeString(i18n.language === 'de' ? 'de-DE' : 'en-US', { 
    hour: '2-digit', 
    minute: '2-digit', 
    hour12: i18n.language !== 'de' 
  });
  
  return `${dateStr} ${startTimeStr} - ${endTimeStr}`;
};

const MessageBubble: React.FC<{
  message: ChatMessage;
  onAcceptSuggestion?: (suggestion: ChatSuggestion) => void;
  onDeclineSuggestion?: (suggestion: ChatSuggestion) => void;
  isLoading?: boolean;
  teamMembers?: Array<{id: string; name: string}>;
  teamMembersLoading?: boolean;
}> = ({ message, onAcceptSuggestion, onDeclineSuggestion, isLoading, teamMembers, teamMembersLoading }) => {
  const { t } = useTranslation();
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Bot className="w-4 h-4 text-primary" />
        </div>
      )}
      
      <div className={`max-w-[80%] ${isUser ? 'order-first' : ''}`}>
        <div
          className={`px-4 py-2 rounded-lg ${
            isUser
              ? 'bg-primary text-primary-foreground ml-auto'
              : 'bg-gray-100 text-gray-900'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
        </div>
        
        <div className="text-xs text-gray-500 mt-1 px-1">
          {format(message.timestamp, t('timeFormatHourMinute'), { locale: i18n.language === 'de' ? de : enUS })}
        </div>

        {/* Suggestions Cards */}
        {message.suggestions && message.suggestions.length > 0 && (
          <div className="mt-3 space-y-2">
            {message.suggestions.filter(suggestion => suggestion.suggestion_type === 'block_time').map((suggestion, index) => (
              <Card key={index} className="border-primary/20">
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">
                        {message.suggestions!.length === 1 ? t('chatbotBlockTimeSuggestion') : `${t('chatbotBlockTimeSuggestion')} ${index + 1}`}
                      </h4>
                      <p className="text-xs text-gray-600 mb-2">
                        {formatBlockTimeDisplay(suggestion.start_time!, suggestion.end_time!)}
                      </p>
                      {/* Team members info - always show when teamMembers data is available */}
                      <p className="text-xs text-gray-500 mb-2">
                        <span className="font-medium">{t('chatbotTeamMembers')}: </span>
                        {teamMembersLoading || !teamMembers ? 
                          'Loading...' :
                          (!suggestion.team_member_ids || suggestion.team_member_ids.length === 0 || suggestion.team_member_ids.length === teamMembers.length ? 
                            t('allTeamMembers') : 
                            suggestion.team_member_ids.map(id => 
                              teamMembers.find(member => member.id === id)?.name
                            ).filter(Boolean).join(', ')
                          )
                        }
                      </p>
                      {suggestion.reason && (
                        <p className="text-xs text-gray-500 mb-2">
                          {suggestion.reason}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Show action buttons or status based on suggestion state */}
                  {suggestion.status === 'pending' && (
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => onAcceptSuggestion?.(suggestion)}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Check className="w-3 h-3 mr-1" />
                        )}
                        {t('chatbotAcceptSuggestion')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => onDeclineSuggestion?.(suggestion)}
                        disabled={isLoading}
                      >
                        <X className="w-3 h-3 mr-1" />
                        {t('chatbotDeclineSuggestion')}
                      </Button>
                    </div>
                  )}
                  
                  {/* Show visual confirmation for accepted/declined suggestions */}
                  {suggestion.status === 'accepted' && (
                    <div className="mt-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium">{t('chatbotSuggestionAccepted')}</span>
                      </div>
                    </div>
                  )}
                  
                  {suggestion.status === 'declined' && (
                    <div className="mt-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-600">
                        <X className="w-4 h-4" />
                        <span className="text-sm font-medium">{t('chatbotSuggestionDeclined')}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
          <User className="w-4 h-4 text-primary-foreground" />
        </div>
      )}
    </div>
  );
};

export const AIChatbotSidebar: React.FC<AIChatbotSidebarProps> = ({ className, onClose }) => {
  const { t, i18n } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  const {
    messages,
    isLoading,
    sendMessage,
    acceptSuggestion,
    declineSuggestion,
    clearMessages,
  } = useChatbot();

  const { currentTenant } = useTenant();
  const { teamMembers, isLoading: teamMembersLoading } = useTeamMembers(currentTenant?.id || null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when sidebar opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-resize textarea with maximum of 5 lines
  const adjustTextareaHeight = () => {
    const textarea = inputRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      
      // Calculate line height (approximate)
      const lineHeight = 20; // pixels per line (you can adjust this)
      const maxHeight = lineHeight * 8; // 8 lines maximum
      
      // Set height based on content, but cap at maxHeight
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
      
      // Enable scrolling if content exceeds max height
      textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }
  };

  // Adjust height when input value changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const messageText = inputValue.trim();
    setInputValue('');
    
    // Send message with current date context and timezone
    const now = new Date();
    const contextData = {
      current_date: now.toISOString(),
      current_local_time: now.toLocaleString(i18n.language === 'de' ? 'de-DE' : 'en-US'),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezone_offset: now.getTimezoneOffset(),
      language: i18n.language, // Pass current UI language
    };
    
    await sendMessage(messageText, contextData);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    // Shift+Enter allows new lines (default behavior)
  };

  const handleClearMessages = () => {
    clearMessages();
    // Add a welcome message
    setTimeout(() => {
      // This would be handled by the hook initialization
    }, 100);
  };

  return (
    <div className={`flex flex-col h-full bg-white ${className}`}>
      {/* Header */}
      <div className="border-b p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-gray-900">{t('chatbotTitle')}</h3>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearMessages}
                title="Clear conversation"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                title="Close chat"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="px-4">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-center py-12">
                <div className="p-6">
                  <Bot className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    {t('chatbotTitle')}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {t('chatbotPlaceholder')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="py-4">
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    onAcceptSuggestion={acceptSuggestion}
                    onDeclineSuggestion={declineSuggestion}
                    isLoading={isLoading}
                    teamMembers={teamMembers}
                    teamMembersLoading={teamMembersLoading}
                  />
                ))}
                {isLoading && (
                  <div className="flex gap-3 justify-start mb-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div className="bg-gray-100 px-4 py-2 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                        <span className="text-sm text-gray-600">{t('chatbotThinking')}</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Footer */}
      <div className="border-t p-4">
        <div className="flex gap-2 items-end">
          <Textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatbotPlaceholder')}
            disabled={isLoading}
            className="flex-1 min-h-[40px] resize-none"
            rows={1}
            style={{ height: '40px', overflowY: 'hidden' }}
          />
          <Button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            size="sm"
            className="self-end"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
